import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function formatDateTime(v) {
  if (!v) return '—';
  return new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function EvaluateForm({ question, onSubmit, submitting }) {
  const [marks, setMarks] = useState(question.marks_awarded ?? '');
  const [feedback, setFeedback] = useState(question.evaluator_comment || '');
  const [reason, setReason] = useState('');

  return (
    <div className="mt-16 p-16" style={{ background: '#fff8f0', borderRadius: 12, border: '1px solid #ffe6cc' }}>
      <div className="fw-semibold mb-8">Manual Evaluation</div>
      <div className="row g-12">
        <div className="col-sm-3">
          <label className="text-13 text-gray-500 mb-4 d-block">Marks (max {question.marks_snapshot})</label>
          <input type="number" min="0" max={question.marks_snapshot} step="0.5" className="form-control py-9" value={marks} onChange={(e) => setMarks(e.target.value)} />
        </div>
        <div className="col-sm-5">
          <label className="text-13 text-gray-500 mb-4 d-block">Feedback to student (optional)</label>
          <input type="text" className="form-control py-9" value={feedback} onChange={(e) => setFeedback(e.target.value)} />
        </div>
        <div className="col-sm-4">
          <label className="text-13 text-gray-500 mb-4 d-block">Reason for this mark <span className="text-red-500">*</span></label>
          <input type="text" className="form-control py-9" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
      </div>
      <div className="d-flex justify-content-end mt-12">
        <button type="button" className="btn btn-sm btn-main rounded-pill py-6 px-16" disabled={submitting}
          onClick={() => onSubmit(question.id, marks, feedback, reason)}>
          {submitting ? 'Saving...' : question.evaluation_status === 'evaluated' ? 'Re-grade' : 'Award Marks'}
        </button>
      </div>
    </div>
  );
}

function QuestionReview({ question, onEvaluate, evaluatingId }) {
  const options = question.options_snapshot || [];
  const correctIds = question.correct_answer_snapshot?.correct_option_ids || [];
  const answer = question.student_answer_json;

  return (
    <div className="card mb-16">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-8">
          <div className="text-13 text-gray-400">Q{question.display_order} · {question.question_type.replace('_', ' ')}</div>
          <div className="text-13 fw-semibold">
            {question.marks_awarded !== null && question.marks_awarded !== undefined ? `${question.marks_awarded} / ${question.marks_snapshot}` : `— / ${question.marks_snapshot}`}
          </div>
        </div>
        <div className="fw-semibold mb-12">{question.question_text_snapshot}</div>

        {['single_choice', 'multiple_choice', 'true_false'].includes(question.question_type) && (
          <div className="d-flex flex-column gap-6 mb-8">
            {options.map((o) => {
              const isCorrect = correctIds.includes(o.id);
              const isSelected = question.question_type === 'multiple_choice'
                ? (answer?.selected_option_ids || []).includes(o.id)
                : answer?.selected_option_id === o.id;
              return (
                <div key={o.id} className="d-flex align-items-center gap-8" style={{
                  padding: '6px 10px', borderRadius: 8,
                  background: isCorrect ? '#e9f9ee' : isSelected ? '#fdeeee' : 'transparent',
                }}>
                  {isSelected && <i className="ph ph-user-circle" title="Student's answer" />}
                  <span className={isCorrect ? 'text-success fw-semibold' : ''}>{o.text}</span>
                  {isCorrect && <span className="badge bg-success-subtle text-success-emphasis">Correct</span>}
                </div>
              );
            })}
          </div>
        )}

        {['short_text', 'fill_blank'].includes(question.question_type) && (
          <div className="mb-8">
            <div>Student answer: <strong>{answer?.text || <span className="text-gray-400">(unanswered)</span>}</strong></div>
            {question.correct_answer_snapshot && (
              <div className="text-13 text-gray-500">Accepted: {(question.correct_answer_snapshot.accepted_answers || []).join(', ')}</div>
            )}
          </div>
        )}

        {question.question_type === 'numerical' && (
          <div className="mb-8">
            <div>Student answer: <strong>{answer?.value ?? <span className="text-gray-400">(unanswered)</span>}</strong></div>
            {question.correct_answer_snapshot && (
              <div className="text-13 text-gray-500">
                Correct: {question.correct_answer_snapshot.value} ± {question.correct_answer_snapshot.tolerance}
              </div>
            )}
          </div>
        )}

        {question.question_type === 'long_answer' && (
          <div className="mb-8">
            <div className="p-12" style={{ background: '#f6faf8', borderRadius: 8 }}>
              {answer?.text || <span className="text-gray-400">(unanswered)</span>}
            </div>
          </div>
        )}

        <div className="text-13">
          {question.is_correct === true && <span className="text-success"><i className="ph ph-check-circle me-1" />Correct</span>}
          {question.is_correct === false && <span className="text-danger"><i className="ph ph-x-circle me-1" />Incorrect</span>}
          {question.evaluation_status === 'pending_evaluation' && <span className="text-warning"><i className="ph ph-clock me-1" />Pending Evaluation</span>}
        </div>

        {question.question_type === 'long_answer' && (
          <EvaluateForm question={question} onSubmit={onEvaluate} submitting={evaluatingId === question.id} />
        )}

        {question.adjustments && question.adjustments.length > 0 && (
          <details className="mt-12">
            <summary className="text-13 text-gray-500" style={{ cursor: 'pointer' }}>Mark adjustment history ({question.adjustments.length})</summary>
            <div className="mt-8">
              {question.adjustments.map((a) => (
                <div key={a.id} className="text-13 text-gray-500 mb-4">
                  {formatDateTime(a.changed_at)} — {a.previous_marks ?? '—'} → <strong>{a.new_marks}</strong> by {a.changed_by_name || 'admin'}: {a.reason}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

function AdminMockExamAttemptDetail() {
  const { attemptId } = useParams();
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [evaluatingId, setEvaluatingId] = useState(null);

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) { setError('Authentication required.'); setLoading(false); return; }
      const response = await axios.get(`${apiUrl}/api/admin/mock-exams/attempts/${attemptId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        setAttempt(response.data.attempt);
        setQuestions(response.data.questions || []);
      } else {
        setError(response.data.message || 'Failed to fetch attempt.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching attempt.');
    } finally {
      setLoading(false);
    }
  }, [attemptId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleEvaluate = async (attemptQuestionId, marks, feedback, reason) => {
    setError('');
    setSuccess('');
    if (marks === '' || isNaN(Number(marks)) || Number(marks) < 0) {
      setError('Please enter a valid mark.');
      return;
    }
    if (!reason || !reason.trim()) {
      setError('A reason is required for this mark.');
      return;
    }
    setEvaluatingId(attemptQuestionId);
    try {
      const token = getToken();
      const response = await axios.put(
        `${apiUrl}/api/admin/mock-exams/attempts/${attemptId}/questions/${attemptQuestionId}/evaluate`,
        { marks, feedback, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setSuccess('Question evaluated. Attempt total recalculated.');
        fetchDetail();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(response.data.message || 'Failed to evaluate.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error evaluating question.');
    } finally {
      setEvaluatingId(null);
    }
  };

  if (loading) {
    return (
      <>
        <SideBar />
        <div className="dashboard-main-wrapper">
          <TopNavBar />
          <div className="dashboard-body text-center py-40"><div className="spinner-border text-main-600" /></div>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><Link to="/admin-mock-exams" className="text-gray-200 fw-normal text-15 hover-text-main-600">Mock Exams</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Attempt Detail</span></li>
              </ul>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {attempt && (
            <div className="card mb-24">
              <div className="card-body">
                <div className="row g-16">
                  <div className="col-sm-3"><div className="text-13 text-gray-400">Student</div><div className="fw-semibold">{attempt.firstName} {attempt.lastName} ({attempt.studentid})</div></div>
                  <div className="col-sm-3"><div className="text-13 text-gray-400">Exam</div><div className="fw-semibold">{attempt.exam_title}</div></div>
                  <div className="col-sm-2"><div className="text-13 text-gray-400">Attempt #</div><div className="fw-semibold">{attempt.attempt_number}</div></div>
                  <div className="col-sm-2"><div className="text-13 text-gray-400">Marks</div><div className="fw-semibold">{attempt.total_marks_scored ?? '—'} / {attempt.total_marks_possible}</div></div>
                  <div className="col-sm-2">
                    <div className="text-13 text-gray-400">Result</div>
                    <div className="fw-semibold">
                      {attempt.pass_fail ? (
                        <span className={`badge ${attempt.pass_fail === 'pass' ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis'}`}>{attempt.pass_fail}</span>
                      ) : <span className="badge bg-neutral-200 text-neutral-600">{attempt.status.replace('_', ' ')}</span>}
                    </div>
                  </div>
                  <div className="col-sm-3"><div className="text-13 text-gray-400">Started</div><div>{formatDateTime(attempt.started_at)}</div></div>
                  <div className="col-sm-3"><div className="text-13 text-gray-400">Submitted</div><div>{formatDateTime(attempt.submitted_at)}</div></div>
                  <div className="col-sm-3"><div className="text-13 text-gray-400">Course</div><div>{attempt.coursename}</div></div>
                  <div className="col-sm-3"><div className="text-13 text-gray-400">Submission Type</div><div className="text-capitalize">{attempt.submission_type || '—'}</div></div>
                </div>
              </div>
            </div>
          )}

          {questions.map((q) => (
            <QuestionReview key={q.id} question={q} onEvaluate={handleEvaluate} evaluatingId={evaluatingId} />
          ))}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminMockExamAttemptDetail;
